import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { Text } from 'tio-ui/components/typography';
import { Chip } from 'tio-ui/components/buttons';
import { formatAsDollars } from 'tio-common/utils/format';
import {
  PhCheckCircle,
  PhClock,
  PhWarning,
  PhDownloadSimple,
  PhShieldCheck,
} from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import type { PslfPaymentValidation } from 'tio-employee/services/career-pathways-api';

interface PaymentValidationCardSignature {
  Args: {
    payment: PslfPaymentValidation;
    onDownloadProof?: (payment: PslfPaymentValidation) => void;
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

export default class PaymentValidationCard extends Component<PaymentValidationCardSignature> {
  get statusIcon() {
    switch (this.args.payment.status) {
      case 'qualifying':
        return PhCheckCircle;
      case 'pending':
        return PhClock;
      default:
        return PhWarning;
    }
  }

  get statusColor() {
    switch (this.args.payment.status) {
      case 'qualifying':
        return 'emerald';
      case 'pending':
        return 'amber';
      default:
        return 'red';
    }
  }

  get chipIntent(): 'success' | 'warning' | 'danger' {
    switch (this.args.payment.status) {
      case 'qualifying':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'danger';
    }
  }

  get formattedDate() {
    return new Date(this.args.payment.paymentDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  get formattedAmount() {
    return formatAsDollars(this.args.payment.amount, { cents: true });
  }

  get passedChecks() {
    return this.args.payment.qualificationChecks.filter((c) => c.passed);
  }

  get failedChecks() {
    return this.args.payment.qualificationChecks.filter((c) => !c.passed);
  }

  handleDownload = () => {
    this.args.onDownloadProof?.(this.args.payment);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200
        {{if
          (eq @payment.status 'qualifying')
          'hover:border-emerald-300 dark:hover:border-emerald-600'
        }}"
      ...attributes
    >
      <div class="flex items-start gap-4">
        {{! Status Icon }}
        <div
          class="p-2.5 rounded-full flex-shrink-0
            {{if (eq this.statusColor 'emerald') 'bg-emerald-100 dark:bg-emerald-900/30'}}
            {{if (eq this.statusColor 'amber') 'bg-amber-100 dark:bg-amber-900/30'}}
            {{if (eq this.statusColor 'red') 'bg-red-100 dark:bg-red-900/30'}}"
        >
          <this.statusIcon
            @size={{24}}
            class="{{if (eq this.statusColor 'emerald') 'text-emerald-600 dark:text-emerald-400'}}
              {{if (eq this.statusColor 'amber') 'text-amber-600 dark:text-amber-400'}}
              {{if (eq this.statusColor 'red') 'text-red-600 dark:text-red-400'}}"
            @weight={{if (eq @payment.status "qualifying") "fill" "regular"}}
          />
        </div>

        {{! Payment Details }}
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <Text class="font-semibold text-gray-900 dark:text-white text-lg">
              {{this.formattedAmount}}
            </Text>
            <Text class="text-gray-500 text-sm">
              paid on
              {{this.formattedDate}}
            </Text>
          </div>

          {{! Qualification Checks }}
          {{#unless @compact}}
            <div class="mt-3 space-y-1.5">
              {{#each @payment.qualificationChecks as |check|}}
                <div class="flex items-center gap-2 text-sm">
                  {{#if check.passed}}
                    <PhCheckCircle @size={{16}} class="text-emerald-500 flex-shrink-0" />
                    <Text class="text-gray-600">
                      <span class="font-medium">{{check.name}}:</span>
                      {{check.reason}}
                    </Text>
                  {{else}}
                    <PhWarning @size={{16}} class="text-red-500 flex-shrink-0" />
                    <Text class="text-red-600 dark:text-red-400">
                      <span class="font-medium">{{check.name}}:</span>
                      {{check.reason}}
                    </Text>
                  {{/if}}
                </div>
              {{/each}}
            </div>
          {{/unless}}

          {{! Compact view - just show check count }}
          {{#if @compact}}
            <div class="flex items-center gap-2 mt-1">
              <PhShieldCheck @size={{14}} class="text-emerald-500" />
              <Text class="text-sm text-gray-500">
                {{this.passedChecks.length}}/{{@payment.qualificationChecks.length}}
                checks passed
              </Text>
            </div>
          {{/if}}
        </div>

        {{! Actions }}
        <div class="flex flex-col items-end gap-2 flex-shrink-0">
          <Chip @intent={{this.chipIntent}} @size="sm">
            {{#if (eq @payment.status "qualifying")}}
              Qualifying #{{@payment.paymentNumber}}
            {{else if (eq @payment.status "pending")}}
              Pending Verification
            {{else}}
              Non-Qualifying
            {{/if}}
          </Chip>

          {{#if (eq @payment.status "qualifying")}}
            {{#if @onDownloadProof}}
              <button
                type="button"
                class="text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 text-sm flex items-center gap-1 transition-colors"
                {{on "click" this.handleDownload}}
              >
                <PhDownloadSimple @size={{16}} />
                Download Proof
              </button>
            {{/if}}
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
